import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Music2, Sparkles, Check, X, Loader2, Trophy, Share2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import PublicShell from "@/components/PublicShell";
import PlaylistBar from "@/components/PlaylistBar";
import PopularPlaylists from "@/components/PopularPlaylists";
import TrackCard from "@/components/TrackCard";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { abEvent } from "@/lib/ab";
import { visitorId } from "@/lib/showcase";

const base = API.replace(/\/api$/, "");

function PendingPanel({ reload }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/showcase/pending").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try { await api.post(`/showcase/${id}/review`, { decision }); toast.success(decision === "approve" ? "Track published in the gallery." : "Submission rejected."); load(); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!items.length) return null;
  return (
    <section className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5 mb-10" data-testid="showcase-pending">
      <h2 className="font-display font-bold flex items-center gap-2"><Sparkles size={16} className="text-amber-400" /> Pending review <span className="text-xs font-mono text-muted-foreground">{items.length}</span></h2>
      <div className="mt-3 space-y-3">
        {items.map((t) => (
          <div key={t.id} className="rounded-xl border border-border bg-card p-3 grid md:grid-cols-[1fr_auto] gap-3 items-center" data-testid={`pending-${t.id}`}>
            <div className="min-w-0"><div className="font-medium truncate">{t.title} <span className="text-xs text-muted-foreground">· {t.artist} · {t.genre}</span></div>
              {t.preview_url && <audio controls preload="none" src={`${base}${t.preview_url}`} className="mt-2 w-full h-8" />}</div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => decide(t.id, "approve")} className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`pending-${t.id}-approve`}><Check size={13} /> Approve</Button>
              <Button size="sm" variant="outline" onClick={() => decide(t.id, "reject")} className="h-8 gap-1" data-testid={`pending-${t.id}-reject`}><X size={13} /> Reject</Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Showcase() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [top, setTop] = useState([]);
  const [topShares, setTopShares] = useState([]);
  const [genre, setGenre] = useState("");
  const v = visitorId();
  const load = (g = genre) => api.get(`/showcase?visitor=${v}${g ? `&genre=${encodeURIComponent(g)}` : ""}`).then(({ data: d }) => setData(d)).catch(() => setData({ items: [], genres: [], total: 0 }));
  const loadTop = () => { api.get(`/showcase/top?days=7&limit=3&visitor=${v}`).then(({ data: d }) => setTop(d.items)).catch(() => {}); api.get(`/showcase/top-shares?days=7&limit=3&visitor=${v}`).then(({ data: d }) => setTopShares(d.items)).catch(() => {}); };
  useEffect(() => { load(genre); }, [genre]);
  useEffect(() => { loadTop(); }, []);
  const patchList = (arr, t) => arr.map((x) => (x.id === t.id ? { ...x, likes: t.likes, liked: t.liked } : x));
  const patch = (t) => { setData((d) => d && { ...d, items: patchList(d.items, t) }); setTop((arr) => patchList(arr, t)); setTopShares((arr) => patchList(arr, t)); };
  useSeo({
    title: "Showcase — AI game music made with MusicFlow", path: "/showcase",
    description: "Listen to AI-generated game soundtracks made with MusicFlow StudioAi: loopable tracks, cinematic scores, synthwave, ambient and combat music created from a prompt by indie developers. Like, share and remix them.",
    jsonLd: data?.items?.length ? { "@context": "https://schema.org", "@type": "MusicPlaylist", name: "MusicFlow StudioAi Showcase", numTracks: data.items.length, url: "https://musicflowstudioai.com/showcase",
      track: data.items.slice(0, 20).map((t) => ({ "@type": "MusicRecording", name: t.title, genre: t.genre, byArtist: { "@type": "MusicGroup", name: t.artist }, duration: `PT${t.duration || 30}S`, url: `https://musicflowstudioai.com${t.page_url}` })) } : null,
  });
  const go = () => { abEvent("cta"); navigate(user ? "/dashboard" : "/login"); };
  return (
    <PublicShell active="showcase">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8" data-testid="showcase-page">
        <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary"><Music2 size={12} /> Community showcase</span>
        <Link to="/artists" className="ml-3 inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors" data-testid="showcase-to-artists"><Users size={12} /> Creators directory</Link>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-5 leading-[0.98]">Game music, generated.<br /><span className="text-primary">Hear what a prompt can do.</span></h1>
        <p className="text-muted-foreground mt-5 max-w-2xl">Tracks below were created by developers from a single text prompt and published to the gallery. Every one is loopable, downloadable in WAV and licensed for games. Like your favourites — the weekly top 3 is decided by you.</p>

        <PopularPlaylists />
        {(top.length > 0 || topShares.length > 0) && (
          <div className={`mt-12 grid gap-10 ${top.length > 0 && topShares.length > 0 ? "xl:grid-cols-2" : ""}`}>
            {top.length > 0 && (
              <section data-testid="showcase-top">
                <h2 className="font-display text-2xl font-extrabold flex items-center gap-2 mb-6"><Trophy size={20} className="text-rose-400" /> Most liked this week</h2>
                <div className={`grid gap-4 ${topShares.length > 0 ? "md:grid-cols-3 xl:grid-cols-1" : "md:grid-cols-3"}`}>{top.map((t, i) => <TrackCard key={t.id} t={t} i={i} rank={i + 1} onChange={patch} />)}</div>
              </section>
            )}
            {topShares.length > 0 && (
              <section data-testid="showcase-top-shares">
                <h2 className="font-display text-2xl font-extrabold flex items-center gap-2 mb-6"><Share2 size={20} className="text-fuchsia-400" /> Most shared this week</h2>
                <div className={`grid gap-4 ${top.length > 0 ? "md:grid-cols-3 xl:grid-cols-1" : "md:grid-cols-3"}`}>{topShares.map((t, i) => <TrackCard key={t.id} t={t} i={i} rank={i + 1} onChange={patch} />)}</div>
              </section>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-12" data-testid="showcase-filters">
          <button type="button" onClick={() => setGenre("")} className={`h-8 px-3 rounded-full border text-xs ${!genre ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid="showcase-filter-all">All</button>
          {(data?.genres || []).map((g) => <button key={g} type="button" onClick={() => setGenre(g)} className={`h-8 px-3 rounded-full border text-xs capitalize ${genre === g ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid={`showcase-filter-${g}`}>{g}</button>)}
          {data && <span className="ml-auto text-xs font-mono text-muted-foreground" data-testid="showcase-count">{data.total} track{data.total === 1 ? "" : "s"}</span>}
        </div>
        <div className="mt-8">
          {user?.role === "admin" && <PendingPanel reload={() => load(genre)} />}
          {!data && <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="animate-spin" /></div>}
          {data && data.items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center" data-testid="showcase-empty">
              <Music2 className="mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No tracks in the gallery yet{genre ? ` for ${genre}` : ""}. Be the first — generate a track and click “Publish to showcase”.</p>
              <Button onClick={go} className="mt-5 bg-primary text-primary-foreground" data-testid="showcase-empty-cta">Generate a track</Button>
            </div>
          )}
          {data && data.items.length > 0 && <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="showcase-grid">{data.items.map((t, i) => <TrackCard key={t.id} t={t} i={i} onChange={patch} />)}</div>}
        </div>
      </div>
      <PlaylistBar />
    </PublicShell>
  );
}
